import { useDebouncedValue } from "@mantine/hooks"
import dayjs from "dayjs"
import type { TFunction } from "i18next"
import useSWR from "swr"
import { CartApi, NetworkApi, OrderApi, PricingApi } from "../../../apis"
import type { ICartPreview, ICartPreviewLine } from "../../../apis/cart"
import type { PriceBreakdown } from "../../../apis/pricing"
import type { CartItem } from "../../../store/Cart"
import { currencyFormat } from "../../../utils/currency"
import { calcTermDiscount } from "../../../utils/payment-term-discount"
import type { ITermDiscount } from "../../../utils/payment-term-discount"
import type { SupplierCartGroup } from "./types"

// Shared by the cart drawer (canvas OrdB_Cart), the checkout page (OrdB_Checkout)
// and the success page (OrdB_Success): grouping, per-supplier network data,
// payment terms, and the one submit path. Kept out of the components so the
// drawer's «Ноорог хадгалах» and the checkout page send the same payload.

/** AppShell header height (components/layout/index.tsx `header={{ height: 60 }}`). */
export const HEADER_HEIGHT = 60

/** Where the success page keeps its data, so a reload still shows it. */
export const SUCCESS_STORAGE_KEY = "buyer-sales.checkout-result"

export const breakdownKey = (supplierId: string, variantId: string) => `${supplierId}:${variantId}`

export const groupCart = (items: CartItem[]): Record<string, SupplierCartGroup> => {
  const grouped: Record<string, SupplierCartGroup> = {}
  items.forEach((item) => {
    if (!grouped[item.supplierBusinessId]) {
      grouped[item.supplierBusinessId] = { supplierName: item.supplierName, items: [], totalAmount: 0, totalItems: 0 }
    }
    grouped[item.supplierBusinessId].items.push(item)
    grouped[item.supplierBusinessId].totalAmount += item.price * item.quantity
    grouped[item.supplierBusinessId].totalItems += item.quantity
  })
  return grouped
}

/** A supplier's lines are either all consignment offering items or none (the grid never mixes them). */
export const isConsignmentGroup = (group?: SupplierCartGroup | null): boolean =>
  !!group && group.items.length > 0 && group.items.every((it) => it.isConsignment === true)

/**
 * VAT inside a line amount. Mirrors de-order-service order/create.ts: prices are
 * VAT-inclusive, STANDARD_RATED (the default) at the variant's vatPercent (default 10),
 * any other vatType carries none. Display only; the server books its own figure.
 */
export const lineVat = (item: CartItem): number => {
  const type = item.vatType || "STANDARD_RATED"
  if (type !== "STANDARD_RATED") return 0
  const pct = Number(item.vatPercent) > 0 ? Number(item.vatPercent) : 10
  const gross = (Number(item.price) || 0) * (Number(item.quantity) || 0)
  return (gross / (1 + pct / 100)) * (pct / 100)
}

/**
 * VAT of one supplier group after its order-level (term) discount. The server
 * (de-order-service helpers/discount-allocation.ts allocateOrderDiscount) spreads
 * the discount over the lines pro rata and extracts the VAT again from what is
 * left, so the group's VAT shrinks by the same ratio as its gross amount.
 */
export const groupVatAfterDiscount = (group: SupplierCartGroup | undefined, discount: number): number => {
  if (!group) return 0
  const vat = group.items.reduce((sum, item) => sum + lineVat(item), 0)
  const gross = group.totalAmount
  if (!(gross > 0) || !(discount > 0)) return vat
  return vat * Math.max(0, 1 - Math.min(discount, gross) / gross)
}

// ── Supplier network (payment terms, managers, receiver defaults) ──

/**
 * False while the buyer's network with this supplier is inactive (B40:
 * `GET /ord/network/:id` sends `canOrder` and `networkStatus`). An older
 * response without the flag falls back to the status code; a network that
 * failed to load is not called inactive here (the manager checks catch it).
 */
export const networkCanOrder = (network: any): boolean =>
  !network || (network.canOrder !== false && `${network.networkStatus || ""}`.toUpperCase() !== "INACTIVE")

/** «Supplier: the link is inactive…» for the first supplier in the cart whose network is inactive, else null. */
export const inactiveNetworkReason = (
  t: TFunction,
  supplierIds: string[],
  networks: Record<string, any>,
  groups: Record<string, SupplierCartGroup>,
): string | null => {
  const id = supplierIds.find((x) => !networkCanOrder(networks[x]))
  if (!id) return null
  return `${groups[id]?.supplierName || ""}: ${t("order:buyerCustomer.cannotOrderInactive")}`
}

export function useSupplierNetworks(supplierIds: string[]) {
  const key = supplierIds.length > 0 ? ["cart.networks", [...supplierIds].sort().join(",")] : null
  const { data, isLoading } = useSWR(
    key,
    async () => {
      const entries = await Promise.all(
        supplierIds.map(async (id) => {
          try {
            return [id, await NetworkApi.get(id)] as const
          } catch {
            return [id, null] as const
          }
        }),
      )
      return Object.fromEntries(entries) as Record<string, any>
    },
    { revalidateOnFocus: false },
  )
  return { networks: (data || {}) as Record<string, any>, isLoading: !!key && (isLoading || !data) }
}

export type TermOption = {
  id: string
  description: string
  discount: ITermDiscount | null
}

/**
 * Terms the buyer can pick for one supplier. `GET /ord/network/:id` lists the
 * attached terms in `paymentTerms[]`; with none attached the network default
 * (`paymentTermId` + `paymentTerm` + `paymentTermDiscount`) is the only one.
 */
export const termOptions = (network: any): TermOption[] => {
  const terms: any[] = network?.paymentTerms ?? []
  if (terms.length > 0) {
    return terms
      .filter((x) => x?.id)
      .map((x) => ({ id: x.id, description: x.description || "", discount: x.discount ?? null }))
  }
  if (network?.paymentTermId) {
    return [
      {
        id: network.paymentTermId,
        description: network?.paymentTerm?.description || "",
        discount: network?.paymentTermDiscount ?? null,
      },
    ]
  }
  return []
}

/** «10%» or «5,000₮». */
export const discountValueText = (discount: ITermDiscount): string =>
  discount.discountType === "PERCENTAGE" ? `${discount.discountValue}%` : currencyFormat(discount.discountValue)

/** One line naming a supplier's payment terms and their discounts (cart group header). */
export const termSummary = (t: TFunction, network: any, consignment: boolean): string => {
  if (consignment) return t("order:cart.termConsignment")
  if (!network) return ""
  const options = termOptions(network)
  if (options.length === 0) return t("order:cart.termNone")
  const parts = options.map((o) =>
    o.discount?.discountValue
      ? `${o.description}, ${t("order:cart.termDiscountShort", { value: discountValueText(o.discount) })}`
      : o.description,
  )
  return options.length > 1
    ? t("order:cart.termLine", { terms: parts.join(" · ") })
    : t("order:cart.termLineSingle", { terms: parts[0] })
}

/** Supplier business code shown as «#BU-100180». */
export const supplierCode = (network: any): string => network?.refCode || network?.partner?.refCode || ""

/** The term an order goes out with: the buyer's pick, or the only one there is. 2+ unpicked = "". */
export const resolveTermId = (network: any, picked?: string | null): string => {
  if (picked) return picked
  const options = termOptions(network)
  return options.length === 1 ? options[0].id : ""
}

export const termDiscountAmount = (network: any, termId: string, amount: number): { discount: ITermDiscount | null; amount: number } => {
  const option = termOptions(network).find((x) => x.id === termId)
  const discount = option?.discount ?? null
  return { discount, amount: calcTermDiscount(amount, discount) }
}

/**
 * Advance percent of the chosen term, read-only (B09). The server takes it from
 * the term itself (COD terms only; route order/create.ts). The network response
 * carries the condition and percent for its DEFAULT term only, so for another
 * attached term the value is unknown here (null), not 0.
 */
export const advancePercentOf = (network: any, termId: string): number | null => {
  // Each paymentTerms[] entry carries its own condition (logics/network/get.ts, B09);
  // an older backend only sends it for the network default term.
  const picked = termId && termId !== network?.paymentTermId
    ? (network?.paymentTerms ?? []).find((t: any) => t.id === termId)
    : network?.paymentTerm
  if (!picked?.condition) return null
  return picked.condition === "COD" ? Number(picked.advancePercent) || 0 : 0
}

// ── Price breakdowns for cart lines (source labels) ──

export function useCartBreakdowns(items: CartItem[], buyerBusinessId: string | null) {
  const priced = items.filter((i) => !i.isConsignment)
  const key =
    priced.length > 0
      ? ["cart.breakdowns", buyerBusinessId || "", priced.map((i) => `${i.supplierBusinessId}:${i.id}:${i.quantity}`).join("|")]
      : null
  const { data } = useSWR(
    key,
    async () => {
      const bySupplier = new Map<string, CartItem[]>()
      priced.forEach((i) => bySupplier.set(i.supplierBusinessId, [...(bySupplier.get(i.supplierBusinessId) || []), i]))
      const map: Record<string, PriceBreakdown> = {}
      await Promise.all(
        Array.from(bySupplier.entries()).map(async ([sid, lines]) => {
          try {
            const res = await PricingApi.calculateBatch({
              businessId: sid,
              buyerBusinessId,
              variants: lines.map((l) => ({ variantId: l.id, quantity: l.quantity })),
            })
            const rows: PriceBreakdown[] = Array.isArray(res) ? res : (res as any)?.rows || []
            rows.forEach((b) => (map[breakdownKey(sid, b.variantId)] = b))
          } catch {
            /* labels only; the order is priced again on the server */
          }
        }),
      )
      return map
    },
    { revalidateOnFocus: false },
  )
  return data || {}
}

// ── Non-price promotions (D3): gift lines, bundle / threshold discount ──

export type CartPreviews = Record<string, ICartPreview>

const previewLine = (item: CartItem): ICartPreviewLine => ({
  variantId: item.id,
  parentVariantId: item?.variant?.id || item?.variantParentId || item?.variantId || item?.id,
  warehouseId: item.inventories?.[0]?.warehouseId,
  quantity: Number(item.quantity) || 0,
  price: Number(item.price) || 0,
})

/**
 * What the order service adds when each supplier group becomes an order
 * (POST /ord/cart/preview, one call per supplier; consignment groups excluded,
 * they go through split-checkout). Order create applies the same promotions by
 * itself: gift lines here are display only and never enter the cart store or
 * the submit payload. Debounced so a stepper run sends one request.
 */
export function useCartPreviews(groups: Record<string, SupplierCartGroup>, enabled = true): CartPreviews {
  const requests = enabled
    ? Object.entries(groups)
        .filter(([, g]) => !isConsignmentGroup(g) && g.items.length > 0)
        .map(([supplierBusinessId, g]) => ({ supplierBusinessId, lines: g.items.map(previewLine) }))
    : []
  const [serialized] = useDebouncedValue(requests.length > 0 ? JSON.stringify(requests) : "", 400)
  const { data } = useSWR(
    serialized ? ["cart.preview", serialized] : null,
    async ([, body]: [string, string]) => {
      const list = JSON.parse(body) as { supplierBusinessId: string; lines: ICartPreviewLine[] }[]
      const entries = await Promise.all(
        list.map(async (req) => {
          try {
            return [req.supplierBusinessId, await CartApi.preview(req)] as const
          } catch {
            return [req.supplierBusinessId, null] as const
          }
        }),
      )
      const map: CartPreviews = {}
      entries.forEach(([id, res]) => {
        if (res) map[id] = res
      })
      return map
    },
    { revalidateOnFocus: false, keepPreviousData: true },
  )
  if (!enabled) return {}
  // Drop suppliers that left the cart while the debounce was pending.
  const out: CartPreviews = {}
  Object.keys(groups).forEach((id) => {
    if (data?.[id] && !isConsignmentGroup(groups[id])) out[id] = data[id]
  })
  return out
}

/** Bundle + threshold discount of one supplier group, capped at its amount (create.ts caps it the same way). */
export const extraDiscountOf = (previews: CartPreviews, supplierId: string, groupAmount: number): number =>
  Math.min(Math.max(0, Number(previews[supplierId]?.extraOrderDiscount) || 0), Math.max(0, groupAmount))

export const giftPiecesOf = (preview?: ICartPreview): number =>
  (preview?.giftLines || []).reduce((sum, g) => sum + (Number(g.quantity) || 0), 0)

/** «Урамшуулал» row of the totals: in-price lines, applied gifts / discounts, or none. */
export const promotionSummaryText = (t: TFunction, promoPriced: number, previews: CartPreviews): string => {
  const values = Object.values(previews)
  const gifts = values.reduce((sum, p) => sum + giftPiecesOf(p), 0)
  const parts: string[] = []
  if (promoPriced > 0) parts.push(t("order:cart.promotionInPrice", { count: promoPriced }))
  if (gifts > 0) parts.push(t("order:cart.promotionGifts", { count: gifts }))
  if (parts.length === 0 && values.some((p) => Number(p.extraOrderDiscount) > 0)) parts.push(t("order:cart.promotionDiscount"))
  return parts.length > 0 ? parts.join(" · ") : t("order:cart.promotionNone")
}

/** Lines whose unit price came from a promotion / special / segment price. */
export const promoPricedCount = (items: CartItem[], breakdowns: Record<string, PriceBreakdown>): number =>
  items.filter((item) => {
    const type = breakdowns[breakdownKey(item.supplierBusinessId, item.id)]?.selectedPriceType
    return type === "PROMOTION" || type === "SPECIAL" || type === "SEGMENT"
  }).length

/** Lines the order service refuses to send (error.order.zero_price_line). */
export const zeroPriceItems = (items: CartItem[]): CartItem[] => items.filter((item) => !(Number(item.price) > 0))

// ── Submit ──

export type SupplierCheckout = {
  supplierId: string
  deliveryType: string
  /** YYYY-MM-DD, or "" (draft with the supplier's default schedule). */
  deliveryDate: string
  paymentTermId: string
  /** Override the shared receiver (drawer draft: the network's receiver branch and staff). */
  receiverStaffId?: string
  receiverBranchId?: string
}

export type CheckoutResult = {
  supplierId: string
  supplierName: string
  orderId: string | null
  /** Raw server message (an `error.*` key or text); translate with checkoutErrorText. */
  error?: string
}

type SubmitInput = {
  groups: Record<string, SupplierCartGroup>
  suppliers: SupplierCheckout[]
  networks: Record<string, any>
  receiverBranchId: string
  receiverStaffId: string
  senderNote: string
  senderAdditionalNote: string
  send: boolean
  user: { id?: string | null; currentBusinessId?: string | null } | null | undefined
}

const isoDate = (value: string) => (value && dayjs(value).isValid() ? dayjs(value).format() : "")

const lineFor = (item: CartItem) => ({
  variantId: item.id,
  parentVariantId: item?.variant?.id || item?.variantParentId || item?.variantId || item?.id,
  warehouseId: item.inventories?.[0]?.warehouseId,
  quantity: item.quantity,
  price: item.price,
  discountType: item.discountType || "",
  discountValue: item.discountValue || 0,
})

/**
 * One request per supplier (B01). Consignment suppliers go through
 * POST /ord/cart/split-checkout with a single group, so each one carries its
 * own delivery type and date (the endpoint takes them once per call, not per
 * group); every other supplier goes through POST /ord/order with its own
 * delivery, payment term and term discount. Failures are reported per
 * supplier, never thrown.
 */
export async function submitCart(input: SubmitInput): Promise<CheckoutResult[]> {
  const { groups, networks, user } = input
  const settled = await Promise.allSettled(
    input.suppliers.map(async (so): Promise<CheckoutResult> => {
      const group = groups[so.supplierId]
      const network = networks[so.supplierId]
      const base = { supplierId: so.supplierId, supplierName: group?.supplierName || "" }
      const receiverStaffId = so.receiverStaffId || input.receiverStaffId
      const receiverBranchId = so.receiverBranchId || input.receiverBranchId
      const deliveryDate = isoDate(so.deliveryDate)

      if (isConsignmentGroup(group)) {
        const agreementId = group.items.find((it) => it.consignmentAgreementId)?.consignmentAgreementId
        const res: any = await OrderApi.splitCheckout({
          receiverBranchId,
          receiverStaffId,
          deliveryType: so.deliveryType || "DEFAULT_DATE",
          deliveryDate,
          senderNote: input.senderNote || "",
          senderAdditionalNote: input.senderAdditionalNote || "",
          send: input.send,
          groups: [
            {
              supplierBusinessId: so.supplierId,
              isConsignment: true,
              consignmentAgreementId: agreementId,
              lines: group.items.map((item) => ({
                ...lineFor(item),
                consignmentOfferingItemId: item.consignmentOfferingItemId ?? null,
              })),
            },
          ],
        })
        const row = res?.orders?.[0]
        return { ...base, orderId: row?.orderId ?? null, error: row?.orderId ? undefined : row?.error || "error.order.create_failed" }
      }

      const paymentTermId = so.paymentTermId || network?.paymentTermId
      const { discount } = termDiscountAmount(network, paymentTermId, group?.totalAmount || 0)
      const advance = advancePercentOf(network, paymentTermId)
      const res: any = await OrderApi.create({
        type: "PURCHASE",
        myBusinessId: user?.currentBusinessId,
        orderedUserId: user?.id,
        businessId: so.supplierId,
        paymentTermId,
        supplierStaffId: network?.supplierStaff?.id,
        buyerStaffId: network?.buyerStaff?.id,
        deliveryType: so.deliveryType || "DEFAULT_DATE",
        deliveryDate,
        receiverBranchId,
        receiverStaffId,
        senderNote: input.senderNote || "",
        senderAdditionalNote: input.senderAdditionalNote || "",
        shippingAmount: "",
        // The server re-reads the term's rule and advance percent itself
        // (route order/create.ts); these only keep the payload in step with the UI.
        discountType: discount?.discountType || "",
        discountValue: discount?.discountValue != null ? `${discount.discountValue}` : "",
        advancePercent: `${advance ?? 0}`,
        send: input.send,
        attachments: [],
        lines: (group?.items || []).map(lineFor),
      })
      return { ...base, orderId: res?.id ?? null, error: res?.id ? undefined : "error.order.create_failed" }
    }),
  )
  return settled.map((s, i) =>
    s.status === "fulfilled"
      ? s.value
      : {
          supplierId: input.suppliers[i].supplierId,
          supplierName: groups[input.suppliers[i].supplierId]?.supplierName || "",
          orderId: null,
          error: (s.reason as { message?: string })?.message || "",
        },
  )
}

/** Human text for a submit failure: `error.*` keys through the errors namespace, unknown variant ids as names. */
export const checkoutErrorText = (t: TFunction, raw: string | undefined, items: CartItem[]): string => {
  const message = raw || ""
  const ids = message.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi)
  if (ids && ids.length > 0) {
    const names = ids.map((id) => items.find((item) => item.id === id)?.nameMon).filter(Boolean)
    if (names.length > 0) return t("order:productList.checkoutProductsNotFound", { names: names.join(", ") })
  }
  if (message.startsWith("error.")) {
    return t(message, { ns: "errors", defaultValue: t("common:messages.error") })
  }
  return message || t("common:messages.error")
}

export type SuccessState = {
  send: boolean
  results: CheckoutResult[]
  /** Lines of the suppliers whose order went through, for «Дахин захиалах». */
  lines: CartItem[]
}

export const saveSuccessState = (state: SuccessState) => {
  try {
    sessionStorage.setItem(SUCCESS_STORAGE_KEY, JSON.stringify(state))
  } catch {
    /* the page still has router state */
  }
}

export const loadSuccessState = (): SuccessState | null => {
  try {
    const raw = sessionStorage.getItem(SUCCESS_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as SuccessState) : null
  } catch {
    return null
  }
}
